import React, { useMemo } from 'react';
import { useAutoExecuteState, useLayoutTypesEffect } from 'app/area/case-report/hooks';
import { FavouriteResultComponentProps } from 'app/area/favourite/types';
import { CaseTableViewContainer } from 'app/area/case-report/case-table-view/case-table-view-container';
import styles from './case-report-view.scss';
import { ChooseChartType } from 'app/area/reports/containers/chart/components/choose-chart-type';
import { LayoutTypeValue, caseReportVisualisationTranslations } from 'app/area/case-report/constants';
import { BowtieListing } from 'app/area/case-report/bowtie-listing/bowtie-listing';
import { CaseListing } from 'app/area/case-report/case-listing';
import { CaseListingHeader } from 'app/area/case-report/components/case-listing-hits-and-toggle';
import { HeaderVisibilityConfiguration } from 'app/components';
import { RenderMode } from 'app/types-shared';
import { PrimaryActionComponentFactory } from 'app/area/case-report/case-listing/types';
import { useInstanceOptions } from 'app/area/favourite/favourite-context';
import { DrawerWithBar } from 'ui/drawer-with-bar/drawer-with-bar';
import { CaseListingDrawer } from 'app/area/case-report/components/included-domains/case-listing-drawer';
import { useTranslation } from 'app/infrastructure/translate/hooks';
import { STATE } from 'app/constants';

export interface CaseReportExternalProps {
  headerVisibilityConfiguration?: HeaderVisibilityConfiguration;
  windowAsScrollableAncestor?: boolean;
  requestedRenderMode?: RenderMode;
  hideLoadResults?: boolean;
  hideNumberOfHits?: boolean;
  primaryActionComponentFactory?: PrimaryActionComponentFactory;
}

const componentMap = {
  [LayoutTypeValue.table]: CaseTableViewContainer,
  [LayoutTypeValue.bowtie]: BowtieListing,
  default: CaseListing
};
export const CaseReportContainer = ({ caseReportExternalProps, ...statisticalReportExternalProps }: FavouriteResultComponentProps) => {
  const { layoutTypes, selectedLayoutType, setLayoutType, hideVisualization } = useLayoutTypesEffect();
  const showLayoutTypes = layoutTypes?.length > 1 && !caseReportExternalProps?.primaryActionComponentFactory;
  const translation = useTranslation(STATE.caseListing, caseReportVisualisationTranslations);
  const {
    autoExecute,
    toggleAutoExecution
  } = useAutoExecuteState();

  const {
    hideCaseListingHeader
  } = useInstanceOptions();

  const CaseListingComponent = useMemo(() => {
    const Component = componentMap[selectedLayoutType?.value] || componentMap.default;
    return <Component
      {...statisticalReportExternalProps}
      {...caseReportExternalProps}
      autoExecute={autoExecute}
    />;
  }, [selectedLayoutType, statisticalReportExternalProps, caseReportExternalProps, autoExecute]);

  const isTileView = selectedLayoutType?.value === LayoutTypeValue.tiles;
  const getCustomClassName = () => {
    if (isTileView) {
      return styles.caseTileViewContainer;
    }
    return '';
  };

  const CaseReportContent = (
    <div className={`${styles.drawerContent} ${getCustomClassName()}`}>
      <div>
        {!hideCaseListingHeader && (
          <div className={styles.caseReportHeader}>
            {showLayoutTypes ? (
              <div className={styles.left}>
                <ChooseChartType
                  numberOfGroupByFields={0}
                  layoutTypes={layoutTypes}
                  onSetLayoutType={setLayoutType}
                  selectedLayoutType={selectedLayoutType.value}
                  classNames={styles.layoutTypes}
                />
              </div>
            ) : null}
            <div className={styles.right}>
              <CaseListingHeader
                selectedLayout={selectedLayoutType?.value}
                toggleAutoExecution={toggleAutoExecution}
                autoExecute={autoExecute}
              />
            </div>
          </div>
        )}
        {CaseListingComponent}
      </div>
    </div>
  );

  return (
    <div className={styles.caseReportContainer}>
      {!hideVisualization ? (
        <DrawerWithBar
          title={translation.visualisation}
          drawerContent={<CaseListingDrawer translation={translation} />}
          initiallyOpen={false}
        >
          {CaseReportContent}
        </DrawerWithBar>
      )
        : <div>{CaseReportContent}</div>
      }
    </div>
  );
};
